import { Box, Button, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { useTheme } from '@mui/material/styles';
import { MuiIconButton } from '../IconButton';
import { pulse } from './styles';
import { openModal } from '@/store/actions';
import { useDispatch, useSelector } from 'react-redux';
import { authSelector } from '@/store/selectors';

export const FileUpload = ({
  name,
  onChange,
  placeholder = 'Upload File',
  icon,
  acceptedFormats = '*',
  height = '150px',
  buttonLayout = false,
  buttonwidth = '100%',
  mediaType = 'file',
  multiple = false,
  loading = false,
  disabled = false,
  setisAllowed = true,
  setFileName,
  error,
  errorMessage = '',
}: any) => {
  const theme = useTheme();
  const palette = theme.palette;
  const inputRef = useRef<HTMLInputElement>(null);
  const audioDialogRef = useRef<HTMLDivElement>(null);

  const [inputKey, setInputKey] = useState(Date.now());
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const {user, isAuth} = useSelector(authSelector);
  const [showAudioOptions, setShowAudioOptions] = useState(false);
  const [recording, setRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const recordedChunks = useRef<Blob[]>([]);
  const dispatch = useDispatch();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (audioDialogRef.current && !audioDialogRef.current.contains(event.target as Node)) {
        if (!recording) {
          setShowAudioOptions(false);
        }
      }
    }

    if (showAudioOptions) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAudioOptions, recording]);

  const supportedTypes = [
    { mime: 'audio/mp4', ext: 'm4a' },
    { mime: 'audio/mpeg', ext: 'mp3' },
    { mime: 'audio/x-m4a', ext: 'm4a' },
    { mime: 'audio/webm', ext: 'webm' },
    { mime: 'audio/wav', ext: 'wav' },
  ];

  const getIOSVersion = (): number | null => {
    const ua = navigator.userAgent;
    const match = ua.match(/OS (\d+)_/);
    if (match && match[1]) {
      return parseInt(match[1], 10);
    }
    return null;
  };

  const isOldIOSVersion = (): boolean => {
    const version = getIOSVersion();
    return version !== null && version < 14;
  };

  const handleUpload = () => {
    if (isAuth) {
      if (mediaType === 'audio') {
        if (!recording) {
          setShowAudioOptions((prev) => !prev);
        }
      } else {
        inputRef?.current?.click();
      }
    } else {
      setisAllowed(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const fileList = Array.from(files);
    onChange(name, multiple ? fileList : [fileList[0]]);
    if (setFileName) setFileName(fileList.map((file) => file.name));
    setInputKey(Date.now());
  };

  const getSupportedMimeType = () => {
    for (const { mime } of supportedTypes) {
      if (MediaRecorder.isTypeSupported(mime)) {
        return mime;
      }
    }
    return '';
  };

  const getExtensionForMimeType = (mime: string) => {
    const found = supportedTypes.find((t) => t.mime === mime);
    return found ? found.ext : 'webm';
  };

  const startRecording = async () => {
    if (typeof window !== 'undefined' && /iPhone|iPad|iPod/.test(navigator.userAgent)) {
      if (isOldIOSVersion()) {
        dispatch(
          openModal({
            content:
              'Your iOS version does not support in-browser audio recording. Please record your audio using another app and upload it manually.',
          }),
        );
        return;
      } else {
      }
    }
    try {
      const mimeType = getSupportedMimeType() || 'audio/webm';
      const extension = getExtensionForMimeType(mimeType);

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType });
      recordedChunks.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) recordedChunks.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(recordedChunks.current, { type: mimeType });
        const file = new File([blob], `recording-${Date.now()}.${extension}`, { type: mimeType });

        onChange(name, [file]);
        if (setFileName) setFileName([file.name]);
      };

      recorder.start(1000); // Request data every 1 second
      setMediaRecorder(recorder);
      setRecording(true);
    } catch (err) {
      console.error('Microphone access denied or error:', err);
    }
  };

  const stopRecording = () => {
    mediaRecorder?.stop();
    setRecording(false);
    setShowAudioOptions(false);
  };
  const [recordingTime, setRecordingTime] = useState(0);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (recording) {
      interval = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } else {
      setRecordingTime(0);
      if (interval) clearInterval(interval);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [recording]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
      .toString()
      .padStart(2, '0');
    const secs = (seconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  };

  return (
    <Box
      display='flex'
      flexDirection='column'
      width={buttonLayout ? 'fit-conent': '350px'}
      maxWidth={isMobile && buttonLayout ? 'fit-content' : '350px'}
      alignItems='center'
      position='relative'>
      {buttonLayout ? (
        <MuiIconButton
          icon={icon}
          label={placeholder}
          labelColor='#000'
          altIcon={icon}
          iconPosition='left'
          direction='row'
          background='white'
          width={'fit-content'}
          isRounded
          height={40}
          iconHeight={20}
          iconWidth={20}
          sx={{
            borderRadius: '24px',
            '&:hover': {
              backgroundColor: 'white',
            },
          }}
          method={handleUpload}
        />
      ) : (
        <Box
          display='flex'
          justifyContent='center'
          alignItems='center'
          borderRadius='10px'
          px='1rem'
          tabIndex={0}
          overflow='hidden'
          onClick={handleUpload}
          position='relative'
          width='100%'
          sx={{ backgroundColor: 'rgba(254, 254, 254, 0.25)', cursor: 'pointer' }}
          height={height}
          border='1px dashed rgba(217, 217, 217, 1)'>
          <Typography
            sx={{ fontFamily: '"Inter", sans-serif !important' }}
            textAlign='center'
            fontSize='1rem'
            color='rgba(255, 255, 255, 1)'
            display='flex'
            alignItems='center'>
            {icon && (
              <Box sx={{ mr: '23px', display: 'flex', alignItems: 'center' }}>
                <Image priority src={icon} alt='upload icon' width={40} height={40} />
              </Box>
            )}
            {placeholder}
          </Typography>
        </Box>
      )}

      {mediaType === 'audio' && showAudioOptions && (
        <Box
          ref={audioDialogRef}
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: '#fff',
            boxShadow: 3,
            borderRadius: 2,
            padding: 2,
            zIndex: 999,
            width: '90%',
            minWidth: isMobile ? 320 : 400,
            maxWidth: isMobile ? 320 : 400,
          }}>
          {!recording ? (
            <>
              <Button
                fullWidth
                variant='contained'
                sx={{ backgroundColor: 'rgba(33, 45, 109, 1)', mt: 1 }}
                onClick={() => {
                  inputRef?.current?.click();
                  setShowAudioOptions(false);
                }}>
                <Typography variant='button' fontSize={'1rem'}>
                  Upload From Device
                </Typography>
              </Button>
              <Button
                fullWidth
                variant='contained'
                sx={{ backgroundColor: 'rgba(33, 45, 109, 1)', mt: 1 }}
                onClick={() => {
                  startRecording();
                }}>
                <Typography variant='button' fontSize={'1rem'}>
                  Record Audio
                </Typography>
              </Button>
            </>
          ) : (
            <Box
              display='flex'
              flexDirection='column'
              alignItems='center'
              justifyContent='center'
              gap={2}
              sx={{
                border: '1px solid rgba(33, 45, 109, 1)',
                borderRadius: 2,
                padding: 2,
                width: '100%',
              }}>
              <Box display='flex' alignItems='center' flexDirection={'column'} gap={1}>
                <Box
                  sx={{
                    position: 'relative',
                    width: 50,
                    height: 50,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 0,
                  }}>
                  {/* Animated pulse circle */}
                  <Box
                    sx={{
                      position: 'absolute',
                      width: 50,
                      height: 50,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255, 0, 0, 0.15)',
                      animation: `${pulse} 1.5s infinite`,
                      top: 0,
                      left: 0,
                      zIndex: 0,
                    }}
                  />

                  {/* Mic icon on top */}
                  <Image
                    src={'/icons/Mic.svg'}
                    width={30}
                    height={30}
                    alt='mic'
                    style={{ position: 'relative', zIndex: 1 }}
                  />
                </Box>

                <Typography variant='body2' color='#000' fontWeight={500}>
                  {formatTime(recordingTime)}
                </Typography>
              </Box>

              <Button
                fullWidth
                color='error'
                variant='contained'
                sx={{ backgroundColor: 'rgba(33, 45, 109, 1)' }}
                onClick={stopRecording}>
                <Typography variant='button' fontSize={'1rem'}>
                  Stop Recording
                </Typography>
              </Button>
            </Box>
          )}
        </Box>
      )}

      <input
        key={inputKey}
        id='file-upload-input'
        hidden
        multiple={multiple}
        type='file'
        ref={inputRef}
        disabled={disabled || loading}
        accept={acceptedFormats}
        onChange={handleFileChange}
      />

      {error && (
        <Typography variant='caption' marginTop='0.1875rem'>
          {errorMessage}
        </Typography>
      )}
    </Box>
  );
};
